import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import DestinationPicker, { invalidateDestinations } from "@/components/DestinationPicker";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function defaultFor(f) {
  if (f.default !== undefined) return f.default;
  if (f.type === "switch") return false;
  if (f.type === "number") return 0;
  if (f.type === "multiselect") return [];
  return "";
}

export function PageTitle({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
      <div>
        <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1 text-sm max-w-2xl">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export default function CrudPage({
  title, subtitle, endpoint, columns, fields, testid, addLabel = "Add", rowActions = [],
}) {
  const [items, setItems] = useState([]);
  const [optionsMap, setOptionsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const srcs = fields.filter((f) => f.optionsEndpoint);
    Promise.all(
      srcs.map((f) => api.get(f.optionsEndpoint).then((r) => [f.name, r.data]).catch(() => [f.name, []]))
    ).then((entries) => setOptionsMap(Object.fromEntries(entries)));
    // eslint-disable-next-line
  }, [endpoint]);

  const optionsFor = (f) => {
    if (f.options) return f.options;
    const data = optionsMap[f.name] || [];
    return data.map((d) => ({ value: d[f.optionValue || "id"], label: d[f.optionLabel || "name"] }));
  };

  const labelForValue = (f, value) => {
    const opt = optionsFor(f).find((o) => String(o.value) === String(value));
    return opt ? opt.label : value || "—";
  };

  const openAdd = () => {
    setEditing(null);
    setForm(Object.fromEntries(fields.map((f) => [f.name, defaultFor(f)])));
    setOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
    setOpen(true);
  };

  const setF = (name, value) => setForm((s) => {
    const next = { ...s, [name]: value };
    // Reset any field that depends on the one we just changed.
    fields.forEach((f) => { if (f.dependsOn === name) next[f.name] = ""; });
    return next;
  });

  const save = async () => {
    for (const f of fields) {
      if (f.required && !form[f.name] && form[f.name] !== 0) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    const payload = { ...form };
    fields.forEach((f) => {
      if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0);
    });
    setSaving(true);
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      invalidateDestinations();
      toast.success(editing ? "Saved changes" : "Created successfully");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item[columns[0].name] || item.name || "this item"}"?`)) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      invalidateDestinations();
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((it) => JSON.stringify(it).toLowerCase().includes(s));
  }, [items, q]);

  return (
    <div>
      <PageTitle
        title={title}
        subtitle={subtitle}
        actions={
          <Button onClick={openAdd} data-testid={`${testid}-add-btn`}
            className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-hover))] font-semibold">
            <Plus size={16} className="mr-1.5" /> {addLabel}
          </Button>
        }
      />

      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
          className="pl-9 bg-card border-border" data-testid={`${testid}-search`} />
      </div>

      <div className="rounded-xl border border-border overflow-hidden bg-card/60">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${testid}-table`}>
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-left">
                {columns.map((c) => (
                  <th key={c.name} className="px-4 py-3 font-semibold text-muted-foreground whitespace-nowrap">{c.label}</th>
                ))}
                <th className="px-4 py-3 text-right font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center text-muted-foreground">No records yet. Click “{addLabel}” to create one.</td></tr>
              )}
              {filtered.map((it) => (
                <tr key={it.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/30 transition-colors">
                  {columns.map((c) => (
                    <td key={c.name} className="px-4 py-3 whitespace-nowrap">
                      {c.render ? c.render(it, { labelForValue, fields }) : (it[c.name] ?? "—")}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {rowActions.map((a) => (
                        <Button key={a.key} size="icon" variant="ghost" title={a.label}
                          onClick={() => a.onClick(it)} data-testid={`${testid}-${a.key}-${it.id}`}
                          className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--gold))]">
                          <a.icon size={16} />
                        </Button>
                      ))}
                      <Button size="icon" variant="ghost" title="Edit" onClick={() => openEdit(it)}
                        data-testid={`${testid}-edit-${it.id}`} className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--gold))]">
                        <Pencil size={16} />
                      </Button>
                      <Button size="icon" variant="ghost" title="Delete" onClick={() => del(it)}
                        data-testid={`${testid}-delete-${it.id}`} className="h-8 w-8 text-muted-foreground hover:text-destructive">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto bg-card border-border" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Edit ${title.replace(/s$/, "")}` : `New ${title.replace(/s$/, "")}`}</DialogTitle>
            <DialogDescription className="sr-only">Form to create or edit a {title.replace(/s$/, "")}.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.filter((f) => !(f.editOnly && !editing)).map((f) => (
              <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                <Label className="text-xs text-muted-foreground mb-1.5 block">{f.label}{f.required && " *"}</Label>
                {f.type === "textarea" ? (
                  <Textarea value={form[f.name] ?? ""} onChange={(e) => setF(f.name, e.target.value)}
                    className="bg-secondary/40 border-border" data-testid={`field-${f.name}`} />
                ) : f.type === "switch" ? (
                  <div className="flex items-center gap-2 h-10">
                    <Switch checked={!!form[f.name]} onCheckedChange={(v) => setF(f.name, v)} data-testid={`field-${f.name}`} />
                    <span className="text-sm text-muted-foreground">{form[f.name] ? "Enabled" : "Disabled"}</span>
                  </div>
                ) : f.type === "select" ? (
                  <Select value={form[f.name] ? String(form[f.name]) : ""} onValueChange={(v) => setF(f.name, v)}>
                    <SelectTrigger className="bg-secondary/40 border-border" data-testid={`field-${f.name}`}>
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {optionsFor(f).map((o) => (
                        <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "multiselect" ? (
                  <div className="flex flex-wrap gap-2 p-2 rounded-lg bg-secondary/40 border border-border">
                    {optionsFor(f).map((o) => {
                      const active = (form[f.name] || []).map(String).includes(String(o.value));
                      return (
                        <button type="button" key={String(o.value)}
                          onClick={() => {
                            const cur = (form[f.name] || []).map(String);
                            setF(f.name, active ? cur.filter((x) => x !== String(o.value)) : [...cur, String(o.value)]);
                          }}
                          className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${active
                            ? "bg-[hsl(var(--gold)/0.15)] border-[hsl(var(--gold)/0.5)] text-[hsl(var(--gold))]"
                            : "border-border text-muted-foreground hover:text-foreground"}`}>
                          {o.label}
                        </button>
                      );
                    })}
                    {optionsFor(f).length === 0 && <span className="text-xs text-muted-foreground">No options</span>}
                  </div>
                ) : f.type === "destination" ? (
                  <DestinationPicker type={form[f.dependsOn]} value={form[f.name]}
                    onChange={(v) => setF(f.name, v)} testid={`field-${f.name}`} />
                ) : f.type === "color" ? (
                  <div className="flex items-center gap-2">
                    <input type="color" value={form[f.name] || "#D4AF37"} onChange={(e) => setF(f.name, e.target.value)}
                      className="h-10 w-14 rounded bg-secondary/40 border border-border cursor-pointer" data-testid={`field-${f.name}`} />
                    <Input value={form[f.name] || ""} onChange={(e) => setF(f.name, e.target.value)} className="bg-secondary/40 border-border" />
                  </div>
                ) : (
                  <Input type={f.type === "number" ? "number" : f.type === "password" ? "text" : "text"}
                    value={form[f.name] ?? ""} onChange={(e) => setF(f.name, e.target.value)}
                    placeholder={f.placeholder} className="bg-secondary/40 border-border" data-testid={`field-${f.name}`} />
                )}
                {f.help && <p className="text-[11px] text-muted-foreground mt-1">{f.help}</p>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid={`${testid}-save-btn`}
              className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--gold-hover))] font-semibold">
              {saving ? "Saving…" : editing ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function Pill({ children, tone = "gold" }) {
  const tones = {
    gold: "bg-[hsl(var(--gold)/0.14)] text-[hsl(var(--gold))] border-[hsl(var(--gold)/0.3)]",
    green: "bg-emerald-500/12 text-emerald-400 border-emerald-500/30",
    red: "bg-red-500/12 text-red-400 border-red-500/30",
    gray: "bg-secondary text-muted-foreground border-border",
    blue: "bg-blue-500/12 text-blue-400 border-blue-500/30",
  };
  return <Badge variant="outline" className={`font-medium ${tones[tone]}`}>{children}</Badge>;
}
